import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectActiveLoaders, selectIsAnyLoading } from 'redux/selectors/loadingStates';
import {
    startLoading,
    stopLoading,
    resetAllLoading,
    resetLoading,
    resetLoadingKeys,
} from 'redux/slices/loadingStates';

/**
 * Reusable hook for managing multiple loading states via Redux
 *
 * Features:
 * - Simple API for managing multiple loading states
 * - Memory efficient (only tracks active states)
 * - Prevents unnecessary re-renders
 * - Works across entire application via Redux
 * - Shared state: All components can access the same loading keys
 *
 * @example
 * // Use in any component - automatically shared via Redux
 * const loading = useLoadingStates();
 *
 * // Start loading (shared across all components)
 * loading.start('fetchUser');
 *
 * // Check if loading (works in any component)
 * if (loading.isLoading('fetchUser')) { ... }
 *
 * // Stop loading
 * loading.stop('fetchUser');
 *
 * // Multiple operations
 * loading.start('fetchUser');
 * loading.start('fetchPosts');
 * const isAnyLoading = loading.isAnyLoading(); // true
 */
export function useLoadingStates() {
    const dispatch = useDispatch();
    const activeLoaders = useSelector(selectActiveLoaders);
    const isAnyLoadingGlobal = useSelector(selectIsAnyLoading);

    /**
     * Start loading for a specific key
     * @param {string} key - Unique identifier for the loading operation
     */
    const start = useCallback(
        (key) => {
            dispatch(startLoading(key));
        },
        [dispatch]
    );

    /**
     * Stop loading for a specific key
     * @param {string} key - Unique identifier for the loading operation
     */
    const stop = useCallback(
        (key) => {
            dispatch(stopLoading(key));
        },
        [dispatch]
    );

    /**
     * Check if a specific key is loading
     * @param {string} key - Unique identifier for the loading operation
     * @returns {boolean}
     */
    const isLoading = useCallback(
        (key) => {
            return Boolean(activeLoaders[key]);
        },
        [activeLoaders]
    );

    /**
     * Check if any loading state is active
     * @returns {boolean}
     */
    const isAnyLoading = useCallback(() => {
        return isAnyLoadingGlobal;
    }, [isAnyLoadingGlobal]);

    /**
     * Reset all loading states
     */
    const resetAll = useCallback(() => {
        dispatch(resetAllLoading());
    }, [dispatch]);

    /**
     * Reset specific loading state
     * @param {string} key - Unique identifier for the loading operation
     */
    const reset = useCallback(
        (key) => {
            dispatch(resetLoading(key));
        },
        [dispatch]
    );

    /**
     * Reset a group of loading states in one dispatch
     * @param {string[]} keys - Loading keys to clear
     */
    const resetKeys = useCallback(
        (keys) => {
            dispatch(resetLoadingKeys(keys));
        },
        [dispatch]
    );

    /**
     * Execute an async operation with automatic loading state management
     * @param {string} key - Unique identifier for the loading operation
     * @param {Function} asyncFn - Async function to execute
     * @param {Object} options - Optional callbacks
     * @param {Function} options.onSuccess - Called on success
     * @param {Function} options.onError - Called on error
     * @returns {Promise}
     */
    const execute = useCallback(
        async (key, asyncFn, options = {}) => {
            start(key);
            try {
                const result = await asyncFn();
                if (options.onSuccess) {
                    options.onSuccess(result);
                }
                return result;
            } catch (error) {
                if (options.onError) {
                    options.onError(error);
                } else {
                    throw error;
                }
                return null;
            } finally {
                stop(key);
            }
        },
        [start, stop]
    );

    // Memoized loading states object for performance
    const states = useMemo(() => activeLoaders, [activeLoaders]);

    return {
        start,
        stop,
        isLoading,
        isAnyLoading,
        reset,
        resetAll,
        resetKeys,
        execute,
        states, // For debugging or advanced use cases
    };
}

export default useLoadingStates;
